import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  TableSortLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import QrCodeIcon from "@mui/icons-material/QrCode";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../axiosInstance";
import { toast, ToastContainer } from "react-toastify";

// Column config — matches the real /admin/mentor-payouts/portal response shape.
const allColumns = [
  {
    key: "payoutId",
    label: "Payout ID",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.payoutId,
  },
  {
    key: "transactionId",
    label: "Transaction Id",
    cellCss: { align: "center", minWidth: "180px" },
    getValue: (r) => r.transactionId,
  },
  {
    key: "mentorId",
    label: "Mentor ID",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.mentorId,
  },
  {
    key: "mentorName",
    label: "Mentor Name",
    cellCss: { align: "center", minWidth: "180px" },
    getValue: (r) => r.mentorName,
  },
  {
    key: "totalAmount",
    label: "Total Amount",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.totalAmount,
  },
  {
    key: "requestedAmount",
    label: "Requested Amount",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.requestedAmount,
  },
  {
    key: "balanceAmount",
    label: "Balance Amount",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.balanceAmount,
  },
  {
    key: "paymentRequestType",
    label: "Type of Request",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.paymentRequestType,
  },
  {
    key: "mobileNumber",
    label: "Mobile Number",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.mobileNumber,
  },
  {
    key: "email",
    label: "Email",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.email,
  },
  {
    key: "status",
    label: "Status",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.status,
  },
  {
    key: "requestDate",
    label: "Request Date",
    cellCss: { align: "center", minWidth: "170px" },
    getValue: (r) => r.requestDate,
  },
];
const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));

const STORAGE_KEY = "MentorPayoutsVisibleColumns";
const FILTER_KEY = "MentorPayoutsTableFilters";

const loadFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(FILTER_KEY)) ?? {};
  } catch {
    return {};
  }
};

const statusCfg = {
  REQUESTED: {
    label: "Requested",
    color: "#616161",
    bg: "#f5f5f5",
    dot: "#9e9e9e",
  },
  APPROVED: {
    label: "Approved",
    color: "#00695c",
    bg: "#e0f2f1",
    dot: "#26a69a",
  },
  PROCESSING: {
    label: "Processing",
    color: "#1565c0",
    bg: "#e3f2fd",
    dot: "#42a5f5",
  },
  PAID: {
    label: "Paid",
    color: "#2e7d32",
    bg: "#e8f5e9",
    dot: "#4caf50",
  },
  FAILED: {
    label: "Failed",
    color: "#d84315",
    bg: "#fbe9e7",
    dot: "#ff7043",
  },
  REJECTED: {
    label: "Rejected",
    color: "#c62828",
    bg: "#ffebee",
    dot: "#ef5350",
  },
};

const moneyFmt = (v) =>
  v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;

const dateTimeFmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const moneyColor = {
  totalAmount: { color: "#1565c0", bg: "#e3f2fd" },
  requestedAmount: { color: "#6a1b9a", bg: "#f3e5f5" },
  balanceAmount: { color: "#2e7d32", bg: "#e8f5e9" },
};

function MoneyCell({ value, colKey }) {
  const cfg = moneyColor[colKey] ?? { color: "#37474f", bg: "#eceff1" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.3,
        borderRadius: "8px",
        background: cfg.bg,
      }}
    >
      <Typography
        sx={{ fontSize: "0.82rem", fontWeight: 700, color: cfg.color }}
      >
        {moneyFmt(value)}
      </Typography>
    </Box>
  );
}

function DateCell({ value }) {
  if (!value) return "—";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.3,
        borderRadius: "8px",
        background: "#fff3e0",
      }}
    >
      <Typography
        sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#e65100" }}
      >
        {dateTimeFmt(value)}
      </Typography>
    </Box>
  );
}

function TransactionIdCell({ value }) {
  if (!value) return "—";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.3,
        borderRadius: "8px",
        background: "#e0f2f1",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 700,
          fontFamily: "monospace",
          color: "#00695c",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function StatusCell({ value }) {
  const v = (value ?? "").toUpperCase();
  const cfg = statusCfg[v] ?? {
    label: value ?? "—",
    color: "#555",
    bg: "#f5f5f5",
    dot: "#9e9e9e",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.2,
        py: 0.4,
        borderRadius: "20px",
        background: cfg.bg,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{ fontSize: "0.78rem", fontWeight: 700, color: cfg.color }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

function PaymentTypeChip({ value }) {
  const v = (value ?? "").toUpperCase();
  const isBank = v === "BANK";
  return (
    <Chip
      size="small"
      icon={isBank ? <AccountBalanceIcon /> : <QrCodeIcon />}
      label={value || "—"}
      sx={{
        fontWeight: 600,
        color: isBank ? "#1565c0" : "#6a1b9a",
        backgroundColor: isBank ? "#e3f2fd" : "#f3e5f5",
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
}

function MentorPayoutsTable({ startDate, endDate }) {
  const _f = loadFilters();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);

  const [mentorName, setMentorName] = useState(_f.mentorName ?? "");
  const [statusFilter, setStatusFilter] = useState(_f.statusFilter ?? "");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState(
    _f.paymentTypeFilter ?? "",
  );
  const [page, setPage] = useState(_f.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(_f.rowsPerPage ?? 30);
  const [dense, setDense] = useState(_f.dense ?? true);
  const [orderBy, setOrderBy] = useState("requestDate");
  const [order, setOrder] = useState("desc");

  const effectiveStart = startDate ?? "";
  const effectiveEnd = endDate ?? "";

  // Persist filters
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTER_KEY,
        JSON.stringify({
          mentorName,
          statusFilter,
          paymentTypeFilter,
          page,
          rowsPerPage,
          dense,
        }),
      );
    } catch {}
  }, [mentorName, statusFilter, paymentTypeFilter, page, rowsPerPage, dense]);

  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(0);
  }, [effectiveStart, effectiveEnd]);

  // Fetch payout requests. Response: { data: [...] } (a flat array).
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    const params = { page, size: rowsPerPage };
    if (effectiveStart) params.startDate = effectiveStart;
    if (effectiveEnd) params.endDate = effectiveEnd;
    if (mentorName) params.mentorName = mentorName;
    if (statusFilter) params.status = statusFilter;
    if (paymentTypeFilter) params.paymentType = paymentTypeFilter;

    api
      .get(`/admin/mentor-payouts/portal`, { params })
      .then((res) => {
        const content = res.data?.data?.content ?? [];
        setRows(content);
        setTotalElements(res.data?.data?.totalElements ?? content.length);
      })
      .catch((err) => {
        console.error("Payout requests fetch error:", err);
        setRows([]);
        setTotalElements(0);
        setError("Failed to load payout requests. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [
    effectiveStart,
    effectiveEnd,
    page,
    rowsPerPage,
    mentorName,
    statusFilter,
    paymentTypeFilter,
  ]);

  const sortedData = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      if (!orderBy) return 0;
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [rows, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const [statusDialogRow, setStatusDialogRow] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [newTransactionId, setNewTransactionId] = useState("");
  const [newPaymentType, setNewPaymentType] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const [bankDialogRow, setBankDialogRow] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  const handleOpenBankDialog = async (row) => {
    setBankDialogRow(row);
    setBankAccounts([]);
    setLoadingBankAccounts(true);
    try {
      const res = await api.get(`/admin/mentors/${row.mentorId}/bank-accounts`);
      setBankAccounts(res.data?.data ?? []);
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage || "Failed to load bank details.",
      );
    } finally {
      setLoadingBankAccounts(false);
    }
  };
  const handleCloseBankDialog = () => {
    setBankDialogRow(null);
    setBankAccounts([]);
  };

  const handleOpenStatusDialog = (row) => {
    setStatusDialogRow(row);
    setNewStatus(row.status ?? "");
    setNewTransactionId(row.transactionId ?? "");
    setNewPaymentType(row.paymentRequestType ?? "");
  };
  const handleCloseStatusDialog = () => {
    setStatusDialogRow(null);
    setNewStatus("");
    setNewTransactionId("");
    setNewPaymentType("");
  };
  const handleSaveStatus = async () => {
    if (!statusDialogRow) return;
    try {
      setSavingStatus(true);
      await api.put(
        `/admin/mentor-payouts/portal/${statusDialogRow.payoutId}`,
        {
          status: newStatus,
          transactionId: newTransactionId,
          paymentType: newPaymentType,
        },
      );
      setRows((prev) =>
        prev.map((r) =>
          r.payoutId === statusDialogRow.payoutId
            ? {
                ...r,
                status: newStatus,
                transactionId: newTransactionId,
                paymentRequestType: newPaymentType,
              }
            : r,
        ),
      );
      toast.success("Payout status updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
      handleCloseStatusDialog();
    } catch (error) {
      toast.error(
        error.response?.data?.errorMessage ||
          "Failed to update payout status. Please try again.",
        { autoClose: 3000 },
      );
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flex: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Mentor Name"
                placeholder="Search by mentor name..."
                value={mentorName}
                onChange={(e) => {
                  setMentorName(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, fontSize: 18, color: "#999" }} />
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl
                size="small"
                sx={{ minWidth: 160, textAlign: "left" }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(statusCfg).map(([key, cfg]) => (
                    <MenuItem key={key} value={key}>
                      {cfg.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ minWidth: 160, textAlign: "left" }}
              >
                <InputLabel>Payment Type</InputLabel>
                <Select
                  label="Payment Type"
                  value={paymentTypeFilter}
                  onChange={(e) => {
                    setPaymentTypeFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="BANK">Bank</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                </Select>
              </FormControl>
              <Typography
                sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}
                variant="body2"
                color="text.secondary"
              >
                {sortedData.length} of {totalElements} requests
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ViewColumnIcon />}
                  onClick={handleOpenColumnsMenu}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Columns
                </Button>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={openColumnsMenu}
                  onClose={handleCloseColumnsMenu}
                  PaperProps={{
                    sx: {
                      width: 200,
                      maxHeight: 500,
                      overflowY: "auto",
                      borderRadius: 2,
                    },
                  }}
                >
                  {allColumns.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                        />
                      }
                      label={col.label}
                      sx={{ pl: 1, pr: 2 }}
                    />
                  ))}
                </Menu>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={dense}
                    onChange={(e) => setDense(e.target.checked)}
                    color="primary"
                  />
                }
                label="Dense"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert
          severity="error"
          sx={{ mx: "15px", mt: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginTop: "16px",
              marginBottom: "20px",
            }}
          >
            <TableContainer
              sx={{ maxHeight: "calc(100vh - 160px)", overflowX: "auto" }}
            >
              <Table
                stickyHeader
                aria-label="mentor payouts table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": { color: "#ffffff !important" },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        padding: "10px 5px",
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 100,
                        textAlign: "center",
                        position: "sticky",
                        right: 0,
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(sortedData) && sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow
                        hover
                        key={row.payoutId}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{ padding: "10px 10px", ...c.cellCss }}
                          >
                            {(() => {
                              const value = c.getValue(row);
                              if (c.key === "status")
                                return <StatusCell value={value} />;
                              if (c.key === "paymentRequestType")
                                return <PaymentTypeChip value={value} />;
                              if (
                                c.key === "totalAmount" ||
                                c.key === "balanceAmount" ||
                                c.key === "requestedAmount"
                              )
                                return (
                                  <MoneyCell value={value} colKey={c.key} />
                                );
                              if (c.key === "requestDate")
                                return <DateCell value={value} />;
                              if (c.key === "transactionId")
                                return <TransactionIdCell value={value} />;
                              return value ?? "—";
                            })()}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            padding: "10px 10px",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#ffffff",
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip
                              title="Update Status"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenStatusDialog(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="View Bank Details"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenBankDialog(row)}
                              >
                                <AccountBalanceIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="h6" color="text.secondary">
                          No payout requests found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}

      <Dialog
        open={Boolean(statusDialogRow)}
        onClose={handleCloseStatusDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #17479e 0%, #1565c0 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
            >
              Update Payout
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseStatusDialog}>
            <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#f5f7fb",
              borderRadius: "10px",
              px: 2,
              py: 1.2,
              mb: 2.5,
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{ fontSize: "0.7rem", color: "#8a94a6", fontWeight: 600 }}
              >
                PAYOUT ID
              </Typography>
              <Typography
                sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#17479e" }}
              >
                {statusDialogRow?.payoutId ?? "—"}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{ fontSize: "0.7rem", color: "#8a94a6", fontWeight: 600 }}
              >
                MENTOR
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                {statusDialogRow?.mentorName ?? "—"}
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Transaction ID"
            placeholder="Enter transaction ID"
            value={newTransactionId}
            onChange={(e) => setNewTransactionId(e.target.value)}
            InputProps={{
              sx: { borderRadius: "10px", fontFamily: "monospace" },
            }}
            sx={{ mb: 2.5 }}
          />

          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#37474f",
              mb: 1,
            }}
          >
            Status
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2.5 }}
          >
            {Object.entries(statusCfg).map(([key, cfg]) => {
              const selected = newStatus === key;
              return (
                <Box
                  key={key}
                  onClick={() => setNewStatus(key)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    px: 1.4,
                    py: 0.6,
                    borderRadius: "20px",
                    border: selected
                      ? `1.5px solid ${cfg.color}`
                      : "1.5px solid #e0e0e0",
                    background: selected ? cfg.bg : "#fff",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: cfg.dot,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: selected ? cfg.color : "#616161",
                    }}
                  >
                    {cfg.label}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#37474f",
              mb: 1,
            }}
          >
            Type of Request
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
            {[
              { key: "BANK", label: "Bank", Icon: AccountBalanceIcon },
              { key: "UPI", label: "UPI", Icon: QrCodeIcon },
            ].map(({ key, label, Icon }) => {
              const selected = newPaymentType === key;
              return (
                <Box
                  key={key}
                  onClick={() => setNewPaymentType(key)}
                  sx={{
                    cursor: "pointer",
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.7,
                    py: 1,
                    borderRadius: "10px",
                    border: selected
                      ? "1.5px solid #1565c0"
                      : "1.5px solid #e0e0e0",
                    background: selected ? "#e3f2fd" : "#fff",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 18,
                      color: selected ? "#1565c0" : "#9e9e9e",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: selected ? "#1565c0" : "#616161",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button
            onClick={handleCloseStatusDialog}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              color: "#616161",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingStatus || !newStatus}
            onClick={handleSaveStatus}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              background: "linear-gradient(135deg, #17479e 0%, #1565c0 100%)",
              boxShadow: "none",
            }}
          >
            {savingStatus ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(bankDialogRow)}
        onClose={handleCloseBankDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #17479e 0%, #1565c0 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
              >
                Bank Details
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
              >
                {bankDialogRow?.mentorName ?? "—"}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleCloseBankDialog}>
            <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {loadingBankAccounts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : bankAccounts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 3 }}
            >
              No bank accounts added yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {bankAccounts.map((account) => (
                <Box
                  key={account.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {account.accountHolder}
                    </Typography>
                    {account.isDefault && (
                      <Chip label="Default" size="small" color="primary" />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {[
                      { label: "BANK NAME", value: account.bankName },
                      { label: "BRANCH", value: account.branch },
                      { label: "A/C NO", value: account.accountNumberMasked },
                      { label: "IFSC", value: account.ifsc },
                    ].map((f) => (
                      <Box
                        key={f.label}
                        sx={{
                          background: "#f5f7fb",
                          borderRadius: "8px",
                          px: 1.2,
                          py: 0.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: "#8a94a6",
                            fontWeight: 600,
                          }}
                        >
                          {f.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#17479e",
                          }}
                        >
                          {f.value || "—"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MentorPayoutsTable;
